"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Container from "@/components/Container";
import RichText from "@/components/RichText";
import type { Locale } from "@/i18n/routing";
import type { NumbersBlock as NumbersBlockProps } from "@/payload-types";
import { useLocale } from "@/providers/LocaleProvider/index.client";
import "./index.scss";

type AnimatedNumberProps = {
	value: number;
	className?: string;
	duration?: number; // in ms
	locale?: Locale;
	startAnimation?: boolean;
};

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
	value,
	className,
	locale,
	duration = 3000,
	startAnimation = false,
}) => {
	const [displayValue, setDisplayValue] = useState(0);

	useEffect(() => {
		// Only start animation when startAnimation is true
		if (!startAnimation) {
			setDisplayValue(0);
			return;
		}

		// Validate the value is a valid number
		if (Number.isNaN(value) || !Number.isFinite(value)) {
			setDisplayValue(0);
			return;
		}

		let start: number | null = null;
		const step = (timestamp: number) => {
			if (!start) start = timestamp;
			const progress = Math.min((timestamp - start) / duration, 1);
			setDisplayValue(Math.floor(progress * value));
			if (progress < 1) {
				requestAnimationFrame(step);
			} else {
				setDisplayValue(value);
			}
		};
		requestAnimationFrame(step);
	}, [value, duration, startAnimation]);

	// Format number using Intl.NumberFormat for better locale support
	const formatter = new Intl.NumberFormat(locale, {
		style: "decimal",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
	const formattedValue = formatter.format(displayValue);

	return <p className={className}>{formattedValue}</p>;
};

export const NumbersBlock: React.FC<NumbersBlockProps> = (props) => {
	const { numbers, introContent, outroContent } = props;
	const { locale } = useLocale();
	const [isInView, setIsInView] = useState(false);
	const blockRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !isInView) {
					setIsInView(true);
				}
			},
			{ threshold: 0.3, rootMargin: "0px 0px -10% 0px" },
		);

		const currentRef = blockRef.current;
		if (currentRef) {
			observer.observe(currentRef);
		}

		return () => {
			if (currentRef) {
				observer.unobserve(currentRef);
			}
			observer.disconnect();
		};
	}, [isInView]);

	const intro = introContent ? (
		<div className="numbers_intro">
			<RichText data={introContent} />
		</div>
	) : null;

	const outro = outroContent ? (
		<div className="numbers_outro">
			<RichText data={outroContent} />
		</div>
	) : null;

	return (
		<div ref={blockRef} className="numbers_block">
			<Container>
				{intro}
				<div className="numbers_list">
					{numbers?.map((number, i) => {
						// Parse and validate the number value
						const numValue =
							typeof number.number === "string"
								? parseFloat(number.number)
								: Number(number.number);
						const validValue = Number.isNaN(numValue) ? 0 : numValue;
						const title = number.title ? (
							typeof number.title === "string" ? (
								<p className="numbers_title">{number.title}</p>
							) : (
								<RichText className="numbers_title" data={number.title} />
							)
						) : null;

						return (
							// biome-ignore lint/suspicious/noArrayIndexKey: numbers array has no stable id
							<div key={i} className="numbers_item">
								{number.titlePosition === "above" && title}
								<div className="numbers_number">
									<div className="numbers_number-wrapper">
										{number.prefix &&
											(typeof number.prefix === "string" ? (
												<p className="prefix">{number.prefix}</p>
											) : (
												<RichText className="prefix" data={number.prefix} />
											))}
										<AnimatedNumber
											value={validValue}
											locale={locale}
											startAnimation={isInView}
										/>
									</div>
									{number.suffix &&
										(typeof number.suffix === "string" ? (
											<p className="suffix">{number.suffix}</p>
										) : (
											<RichText className="suffix" data={number.suffix} />
										))}
								</div>
								{number.titlePosition !== "above" && title}
							</div>
						);
					})}
				</div>
				{outro}
			</Container>
		</div>
	);
};
